import React from 'react'
import { useQuery } from '@tanstack/react-query'

import { clientApi, ItemTrack, ItemTrackPoint } from '@/api/client'
import { qk } from '@/api/queryKeys'
import Sparkline from '@/components/Sparkline'
import { fmtProb, timeAgo } from '@/utils/format'

interface ScenarioTracksProps {
  itemId: string
  /** scenario_id of the track whose forecast is already shown as the
   *  "primary" in the header card. We dim/collapse that row so it's
   *  clearly the same pill and not counted twice. `null` means the
   *  baseline track is primary; `undefined` means no primary highlight. */
  primaryScenarioId?: string | null
}

function pointValues(points: ItemTrackPoint[]): number[] {
  return points
    .map((p) => (typeof p.probability === 'number' ? p.probability : null))
    .filter((v): v is number => typeof v === 'number')
}

function trackTone(t: ItemTrack): string {
  if (t.kind === 'baseline') return 'var(--fg)'
  const status = String(t.status || '').toLowerCase()
  if (status === 'realized') return 'var(--ok, #059669)'
  if (status === 'disproven') return 'var(--bad, #dc2626)'
  return 'var(--accent)'
}

function TrackRow({
  track,
  isPrimary,
}: {
  track: ItemTrack
  isPrimary: boolean
}) {
  const latest = track.latest
  const values = pointValues(track.updates)
  const tone = trackTone(track)
  const label = track.label || (track.kind === 'baseline' ? 'Baseline' : 'Untitled scenario')

  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 10,
        border: `1px solid ${isPrimary ? tone : 'var(--border)'}`,
        background: isPrimary ? 'rgba(99,102,241,0.06)' : 'transparent',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              textTransform: 'uppercase',
              color: tone,
            }}
          >
            {track.kind === 'baseline' ? 'Baseline' : 'Scenario'}
          </span>
          {isPrimary && <span className="pill ok" style={{ fontSize: 10 }}>primary</span>}
          {track.kind === 'scenario' && track.status && track.status !== 'hypothetical' && (
            <span className="pill" style={{ fontSize: 10 }}>{track.status}</span>
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4, overflowWrap: 'break-word' }}>
          {label}
        </div>
        {Array.isArray(track.assumptions) && track.assumptions.length > 0 && (
          <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.5 }}>
            {track.assumptions.slice(0, 4).map((a, i) => (
              <li key={i} className="muted">
                {typeof a === 'string' ? a : a?.text || ''}
              </li>
            ))}
            {track.assumptions.length > 4 && (
              <li className="muted" style={{ fontStyle: 'italic' }}>
                +{track.assumptions.length - 4} more
              </li>
            )}
          </ul>
        )}
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {typeof latest?.probability === 'number' ? (
            <span className="pill ok">our {fmtProb(latest.probability)}</span>
          ) : (
            <span className="pill">our —</span>
          )}
          {typeof latest?.market_prob === 'number' && (
            <span className="pill">mkt {fmtProb(latest.market_prob)}</span>
          )}
          {Array.isArray(latest?.confidence_interval) &&
            typeof latest.confidence_interval[0] === 'number' &&
            typeof latest.confidence_interval[1] === 'number' && (
              <span
                className="pill"
                title="Confidence interval around the point probability — narrower is more resolved."
              >
                CI {fmtProb(latest.confidence_interval[0])}–{fmtProb(latest.confidence_interval[1])}
              </span>
            )}
          {latest?.as_of && <span className="pill">{timeAgo(latest.as_of)}</span>}
          <span className="pill">{track.updates.length} update{track.updates.length === 1 ? '' : 's'}</span>
        </div>
      </div>
      {values.length >= 2 && (
        <div style={{ flex: '0 0 auto' }}>
          <Sparkline values={values} width={110} height={32} />
        </div>
      )}
    </div>
  )
}

export default function ScenarioTracks({ itemId, primaryScenarioId }: ScenarioTracksProps) {
  const tracksQuery = useQuery({
    queryKey: qk.items.tracks(itemId),
    queryFn: () => clientApi.itemTracks(itemId),
    enabled: !!itemId,
  })

  const tracks = tracksQuery.data?.tracks || []
  // Always render baseline first, then scenarios in insertion order.
  const baseline = tracks.filter((t) => t.kind === 'baseline' && t.updates.length > 0)
  const scenarios = tracks.filter((t) => t.kind === 'scenario')

  if (tracksQuery.isLoading) {
    return (
      <div className="card">
        <h2>Forecast tracks</h2>
        <div className="muted" style={{ fontSize: 12 }}>Loading tracks…</div>
      </div>
    )
  }
  if (tracksQuery.isError) {
    return (
      <div className="card">
        <h2>Forecast tracks</h2>
        <div className="muted" style={{ fontSize: 12 }}>Could not load tracks.</div>
      </div>
    )
  }
  if (baseline.length === 0 && scenarios.length === 0) {
    return null
  }

  return (
    <div className="card">
      <h2>Forecast tracks</h2>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        Each track is a separate forecast under a different set of assumptions.
      </div>
      <div className="stack" style={{ gap: 10 }}>
        {baseline.map((t) => (
          <TrackRow
            key={t.scenario_id || 'baseline'}
            track={t}
            isPrimary={primaryScenarioId === null}
          />
        ))}
        {scenarios.map((t) => (
          <TrackRow
            key={t.scenario_id || t.label || Math.random().toString()}
            track={t}
            isPrimary={!!t.scenario_id && t.scenario_id === primaryScenarioId}
          />
        ))}
      </div>
    </div>
  )
}
